import React, { useEffect, useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import api from "../console/api";
import { resolveAsset } from "../store/useSiteContent";

export const MEDIA_GROUPS = [
  ["logos", "Logos"], ["stained_glass", "Stained glass artwork"], ["farm", "Farm photos"], ["products", "Products"], ["events", "Events"], ["other", "Other"],
];

/** Choose an existing uploaded image. Shows thumbnails, names, group, and where it's used. */
export function ImageLibraryModal({ onPick, onClose, testid = "image-library" }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("");
  const [usage, setUsage] = useState({});

  useEffect(() => {
    let live = true;
    setRows(null);
    api.get("/admin/media", { params: { q, group } }).then((r) => live && setRows(r.data || [])).catch(() => live && setRows([]));
    return () => { live = false; };
  }, [q, group]);

  const showUsage = async (id) => {
    try { const r = await api.get(`/admin/media/${id}/usage`); setUsage((u) => ({ ...u, [id]: r.data.used_in || [] })); } catch { setUsage((u) => ({ ...u, [id]: [] })); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid={testid} onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[85vh] rounded-md shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b border-[#E8DFCB]">
          <label className="relative flex-1">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#9C8B73]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by file name or alt text" data-testid={`${testid}-search`} className="w-full pl-8 pr-2 h-9 border border-[#E8DFCB] rounded text-sm" />
          </label>
          <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid={`${testid}-group`} className="h-9 px-2 border border-[#E8DFCB] rounded text-sm">
            <option value="">All groups</option>
            {MEDIA_GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button type="button" onClick={onClose} aria-label="Close" className="h-9 w-9 grid place-items-center rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 overflow-y-auto">
          {!rows && <div className="text-sm text-[#7A6A55] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading images…</div>}
          {rows && rows.length === 0 && <div className="text-sm text-[#7A6A55]" data-testid={`${testid}-empty`}>No images match. Upload one instead.</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {(rows || []).map((m) => (
              <div key={m.id} className="border border-[#E8DFCB] rounded overflow-hidden text-xs bg-[#FBF6E9]" data-testid={`${testid}-item-${m.id}`}>
                <button type="button" onClick={() => onPick(m)} className="block w-full h-28 bg-[repeating-conic-gradient(#eee_0%_25%,#fff_0%_50%)] bg-[length:16px_16px]" title="Use this image">
                  <img src={resolveAsset(m.url, "")} alt={m.alt || m.filename} className="w-full h-full object-contain" loading="lazy" />
                </button>
                <div className="p-2">
                  <div className="truncate text-[#3C2A1E]" title={m.filename}>{m.filename}</div>
                  <div className="text-[#9C8B73]">{(MEDIA_GROUPS.find(([v]) => v === m.group) || [])[1] || "Other"}{m.width ? ` · ${m.width}×${m.height}` : ""}</div>
                  <button type="button" onClick={() => showUsage(m.id)} className="underline text-[#7A6A55] mt-1" data-testid={`${testid}-usage-${m.id}`}>Where is it used?</button>
                  {usage[m.id] && <div className="mt-0.5 text-[#3C2A1E]">{usage[m.id].length ? usage[m.id].map((u, i) => <div key={i}>{u.where}: {u.ref}</div>) : "Not used anywhere yet."}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
